import { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

// One place for Chart.js registration, motion defaults and theme tokens, shared by every chart.

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Title, Tooltip, Legend, ArcElement);

const prefersReducedMotion =
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (prefersReducedMotion) {
  ChartJS.defaults.animation = false;
} else {
  ChartJS.defaults.animation.duration = 900;
  ChartJS.defaults.animation.easing = 'easeOutQuart';
}

export const readToken = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

export const withAlpha = (hex, alpha) => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return hex;
  const n = parseInt(match[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// Chart.js draws on canvas, so it can't read CSS variables itself. Resolve the tokens
// here and re-resolve whenever the OS switches between light and dark.
export const useChartTheme = () => {
  const [scheme, setScheme] = useState(() =>
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => setScheme(event.matches ? 'dark' : 'light');
    query.addEventListener ? query.addEventListener('change', onChange) : query.addListener(onChange);
    return () => {
      query.removeEventListener ? query.removeEventListener('change', onChange) : query.removeListener(onChange);
    };
  }, []);

  return useMemo(() => ({
    scheme,
    font: readToken('--font-sans', 'system-ui, sans-serif'),
    text: readToken('--text', '#1d1d1f'),
    textSecondary: readToken('--text-secondary', '#6e6e73'),
    surface: readToken('--surface', '#ffffff'),
    grid: readToken('--chart-grid', 'rgba(0,0,0,0.06)'),
    axis: readToken('--chart-axis', 'rgba(0,0,0,0.16)'),
    accent: readToken('--accent-fill', '#0071e3'),
    positive: readToken('--positive', '#248a3d'),
    negative: readToken('--negative', '#d70015'),
    periods: ['--chart-1', '--chart-2', '--chart-3', '--chart-4'].map((name) => readToken(name, '#3987e5')),
    categories: [readToken('--chart-cat-1', '#2a78d6'), readToken('--chart-cat-2', '#eb6834')],
  }), [scheme]);
};

export const tooltipStyle = (theme) => ({
  backgroundColor: theme.scheme === 'dark' ? '#2c2c2e' : '#1d1d1f',
  titleColor: '#f5f5f7',
  bodyColor: '#d1d1d6',
  titleFont: { family: theme.font, size: 12, weight: '600' },
  bodyFont: { family: theme.font, size: 12 },
  padding: 10,
  cornerRadius: 10,
  boxPadding: 4,
  usePointStyle: true,
});

// Vertical hairline under the hovered point, so a line chart reads like a crosshair.
export const crosshairPlugin = {
  id: 'fpCrosshair',
  afterDatasetsDraw(chart, args, options) {
    const active = chart.tooltip && chart.tooltip.getActiveElements();
    if (!active || !active.length) return;
    const { ctx, chartArea } = chart;
    const x = active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = options.color || 'rgba(127,127,127,0.4)';
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.restore();
  },
};
